import React, { useId } from 'react';

// Exact colors from the provided reference assets (orderart_table_status_icon(.svg|_red.svg)):
// green status border for Available, red for Occupied - everything else (panel gradient, glow,
// trapezoid icon) is identical between the two, so both render off one shared badge below.
const AVAILABLE_ACCENT = '#20F57A';
const OCCUPIED_ACCENT = '#FF4D57';

/**
 * The whole tile background (panel + status border + trapezoid icon), lifted directly from the
 * reference SVGs - only the status border color differs between Available/Occupied. Gradient/
 * filter ids are namespaced per instance (useId) since many pagers render on the same page at
 * once and raw `url(#panelGrad)` ids would collide across separate inline <svg> elements.
 */
const PagerBadgeIcon = ({ accent }) => {
  const uid = useId();
  const gradId = `pagerPanelGrad${uid}`;
  const glowId = `pagerSoftGlow${uid}`;

  return (
    <svg viewBox="0 0 1024 1024" className="pagerBadgeIcon" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#222B35" />
          <stop offset="100%" stopColor="#182129" />
        </linearGradient>
        <filter id={glowId} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Outer tile */}
      <rect x="92" y="92" width="840" height="840" rx="130" fill={`url(#${gradId})`} stroke="#0E1720" strokeWidth="28" />

      {/* Status border */}
      <rect x="122" y="122" width="780" height="780" rx="112" fill="none" stroke={accent} strokeWidth="28" filter={`url(#${glowId})`} />

      {/* Table / trapezoid icon */}
      <path
        d="M340 430 H684 L760 610 H264 Z"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="34"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

// A small receipt/docket glyph (a slip with a couple of lines and a torn bottom edge) rather
// than a generic "info" icon, so it visually promises what tapping it opens - the same digital
// receipt shown right after checkout.
const ReceiptIcon = () => (
  <svg viewBox="0 0 24 24" className="pagerReceiptIcon" aria-hidden="true">
    <path
      d="M6 3h12v17l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3-2 1.3V3z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M8.5 8h7M8.5 11.5h7M8.5 15h4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/**
 * The Pager zone's dashboard tile — a badge instead of SimpleTableTile's table-icon layout,
 * since a pager isn't a seat/table, just a numbered physical device staff hand to a customer.
 * Available (green) shows just the number; Occupied (red) adds the customer's name, sourced the
 * same way every other occupied table already gets it (OCCUPIED_TABLE_WITH_ORDERS via
 * useTableOrdering's `info` map) - no new backend data needed.
 *
 * Tap behavior is entirely the caller's call (via `onTap`, see useTableOrdering's
 * handlePagerTap): unlike every other table type, tapping an occupied pager releases it
 * immediately instead of navigating into its order. The small receipt button below an occupied
 * tile is a second, independent action (onViewReceipt) - it must never trigger that release tap,
 * hence its own stopPropagation.
 */
const PagerTile = ({ table, isOccupied, info, onTap, onViewReceipt }) => {
  const pagerNumber = table.name || `${table.tableNumber}`;
  const fullName = info?.customerName || info?.orderName;
  // The badge has room for one short word (see "Gagandeep Sin..." overflowing/truncating in
  // testing) - a full customer profile name is cut to its first name here; a free-typed
  // "Customer Name" is usually already just one word (e.g. "Aaron") and passes through unchanged.
  const customerName = fullName?.trim().split(/\s+/)[0] || null;

  return (
    <div className={`pagerTile${isOccupied ? ' pagerTile--occupied' : ' pagerTile--available'}`}>
      <button
        type="button"
        className="pagerTileMain"
        onClick={() => onTap(table.id, pagerNumber)}
      >
        <PagerBadgeIcon accent={isOccupied ? OCCUPIED_ACCENT : AVAILABLE_ACCENT} />
        {isOccupied && customerName && <span className="pagerTileName">{customerName}</span>}
        <span className="pagerTileNumber">{pagerNumber}</span>
        <span className="pagerTileStatus">{isOccupied ? 'Occupied' : 'Available'}</span>
      </button>
      {isOccupied && onViewReceipt && (
        <button
          type="button"
          className="pagerReceiptBtn"
          aria-label="View order details"
          onClick={(e) => {
            e.stopPropagation();
            onViewReceipt(table.id);
          }}
        >
          <ReceiptIcon />
        </button>
      )}
    </div>
  );
};

export default PagerTile;
